import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { IngredientTraceCode } from '../types/ingredientTraceCode';
import { BatchRecord } from '../types/batchRecord';
import { ShipmentEntry } from '../types/shipment';
import { BakingProductionLog } from '../types/bakingProductionLog';
import { DeliveryLog } from '../types/deliveryLog';
import { TraceCode } from '../types/traceCode';
import { updateIngredientTraceCode, deleteIngredientTraceCode } from '../store/slices/ingredientTraceCodesSlice';
import { updateBatchRecordAsync, deleteBatchRecordAsync } from '../store/slices/batchRecordsSlice';
import { deleteIngredientReceivingEntry } from '../store/slices/ingredientReceivingSlice';
import { deleteBakingProductionLogAsync } from '../store/slices/bakingProductionLogSlice';
import { deleteDeliveryLogAsync } from '../store/slices/deliveryLogsSlice';
import { deleteTraceCodeAsync } from '../store/slices/traceCodesSlice';
import { addToast } from '../store/slices/toastsSlice';
import DeleteRecordConfirmationModal from '../components/DeleteRecordConfirmationModal';

// Record types for categorization (original 2 types)
type RecordType = 'ingredientTraceCode' | 'batchRecord';

// Extended record types including all 6 HACCP log types
type ExtendedRecordType =
  | 'ingredientTraceCode'
  | 'batchRecord'
  | 'ingredientReceiving'
  | 'bakingProductionLog'
  | 'deliveryLog'
  | 'finishedProductTraceCode';

// Category constants for sub-sections
const PRODUCT_TRACKING_TYPES: ExtendedRecordType[] = [
  'batchRecord', 'bakingProductionLog', 'finishedProductTraceCode', 'deliveryLog'
];

const INGREDIENT_TRACKING_TYPES: ExtendedRecordType[] = [
  'ingredientReceiving', 'ingredientTraceCode'
];

const isProductTrackingType = (type: ExtendedRecordType): boolean =>
  PRODUCT_TRACKING_TYPES.includes(type);

const isIngredientTrackingType = (type: ExtendedRecordType): boolean =>
  INGREDIENT_TRACKING_TYPES.includes(type);

// Anomaly types for flagged records
type AnomalyType =
  | 'duplicate_lot_number'
  | 'type_mismatch'
  | 'out_of_range_temperature'
  | 'out_of_range_quantity'
  | 'invalid_time_sequence';

// Interface for flagged records
interface FlaggedRecord {
  type: ExtendedRecordType;
  record: IngredientTraceCode | BatchRecord | ShipmentEntry | BakingProductionLog | DeliveryLog | TraceCode;
  anomalyType: AnomalyType;
  anomalyDescription: string;
  identifier: string;
  flaggedField?: string;
}

// HACCP Temperature ranges (in Fahrenheit)
const TEMPERATURE_RANGES = {
  oven: { min: 250, max: 500 },
  receivingRefrigerated: { min: 32, max: 41 },
  receivingNonRefrigerated: { min: 32, max: 71 },
  receivingFrozen: { min: -20, max: 0 },
  transportFrozen: { min: -20, max: 45 },
  cooling1: { min: 0, max: 70 },
  cooling2: { min: 0, max: 39 },
  freezing: { min: -20, max: 0 },
  baking: { min: 210, max: 240 }
};

// Quantity ranges
const QUANTITY_RANGES = {
  min: 0,
  max: 10000
};

// Get identifier for a record based on its type
const getRecordIdentifier = (record: any, type: ExtendedRecordType): string => {
  switch (type) {
    case 'ingredientTraceCode':
      return record.internalTraceCode || record.id;
    case 'batchRecord':
      return record.batchLotNumber || record.id;
    case 'ingredientReceiving':
      return record.lotNumber || record.id;
    case 'bakingProductionLog':
      return record.lotNumber || record.id;
    case 'deliveryLog':
      return record.batchLotNumber || record.id;
    case 'finishedProductTraceCode':
      return record.batchLotNumber || record.id;
    default:
      return record.id;
  }
};

// Check if a value is a valid number
const isValidNumber = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return true; // Empty is ok
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return typeof num === 'number' && !isNaN(num);
};

// Find duplicate lot numbers across all record types
const findDuplicateLotNumbers = (
  ingredientTraceCodes: IngredientTraceCode[],
  batchRecords: BatchRecord[],
  ingredientReceiving: ShipmentEntry[],
  bakingProductionLogs: BakingProductionLog[],
  deliveryLogs: DeliveryLog[],
  traceCodes: TraceCode[]
): FlaggedRecord[] => {
  const lotNumberMap = new Map<string, { type: ExtendedRecordType; record: any }[]>();

  // Helper to add to map
  const addToMap = (lotNumber: string | undefined | null, type: ExtendedRecordType, record: any) => {
    if (!lotNumber || lotNumber.trim() === '') return;
    const key = lotNumber.toLowerCase().trim();
    if (!lotNumberMap.has(key)) lotNumberMap.set(key, []);
    lotNumberMap.get(key)!.push({ type, record });
  };

  // Collect lot numbers from all types
  ingredientTraceCodes.forEach(r => addToMap(r.internalTraceCode, 'ingredientTraceCode', r));
  batchRecords.forEach(r => addToMap(r.batchLotNumber, 'batchRecord', r));
  ingredientReceiving.forEach(r => addToMap(r.lotNumber, 'ingredientReceiving', r));
  bakingProductionLogs.forEach(r => addToMap(r.lotNumber, 'bakingProductionLog', r));
  deliveryLogs.forEach(r => addToMap(r.batchLotNumber, 'deliveryLog', r));
  traceCodes.forEach(r => addToMap(r.batchLotNumber, 'finishedProductTraceCode', r));

  // Flag duplicates (same lot number appearing in same record type multiple times)
  const flagged: FlaggedRecord[] = [];
  lotNumberMap.forEach((records, lotNumber) => {
    // Group by type to find duplicates within the same type
    const byType = new Map<ExtendedRecordType, any[]>();
    records.forEach(({ type, record }) => {
      if (!byType.has(type)) byType.set(type, []);
      byType.get(type)!.push(record);
    });

    // Map type to its lot number field name
    const lotFieldMap: Record<ExtendedRecordType, string> = {
      ingredientTraceCode: 'internalTraceCode',
      batchRecord: 'batchLotNumber',
      ingredientReceiving: 'lotNumber',
      bakingProductionLog: 'lotNumber',
      deliveryLog: 'batchLotNumber',
      finishedProductTraceCode: 'batchLotNumber'
    };

    // Flag if same lot number appears multiple times in the same record type
    byType.forEach((typeRecords, type) => {
      if (typeRecords.length > 1) {
        typeRecords.forEach(record => {
          flagged.push({
            type,
            record,
            anomalyType: 'duplicate_lot_number',
            anomalyDescription: `Duplicate lot number "${lotNumber}" found (${typeRecords.length} records)`,
            identifier: getRecordIdentifier(record, type),
            flaggedField: lotFieldMap[type]
          });
        });
      }
    });
  });

  return flagged;
};

// Detect type mismatches (non-numeric values in numeric fields)
const detectTypeMismatches = (
  ingredientReceiving: ShipmentEntry[],
  bakingProductionLogs: BakingProductionLog[],
  deliveryLogs: DeliveryLog[],
  batchRecords: BatchRecord[],
  traceCodes: TraceCode[]
): FlaggedRecord[] => {
  const flagged: FlaggedRecord[] = [];

  // Check ingredient receiving
  ingredientReceiving.forEach(record => {
    if (!isValidNumber(record.quantity)) {
      flagged.push({
        type: 'ingredientReceiving',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Quantity field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'ingredientReceiving'),
        flaggedField: 'quantity'
      });
    }
    if (!isValidNumber(record.receivingTemperature)) {
      flagged.push({
        type: 'ingredientReceiving',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Temperature field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'ingredientReceiving'),
        flaggedField: 'receivingTemperature'
      });
    }
  });

  // Check baking production logs
  bakingProductionLogs.forEach(record => {
    if (!isValidNumber(record.ovenTemp)) {
      flagged.push({
        type: 'bakingProductionLog',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Oven temperature field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'bakingProductionLog'),
        flaggedField: 'ovenTemp'
      });
    }
  });

  // Check delivery logs
  deliveryLogs.forEach(record => {
    if (!isValidNumber(record.transportationTemp)) {
      flagged.push({
        type: 'deliveryLog',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Transportation temperature field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'deliveryLog'),
        flaggedField: 'transportationTemp'
      });
    }
    if (!isValidNumber(record.quantity)) {
      flagged.push({
        type: 'deliveryLog',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Quantity field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'deliveryLog'),
        flaggedField: 'quantity'
      });
    }
  });

  // Check batch records temperature fields
  batchRecords.forEach(record => {
    const tempFields = ['bakingActualTemp', 'cooling1ActualTemp', 'cooling2ActualTemp', 'freezingActualTemp'] as const;
    tempFields.forEach(field => {
      const value = record[field];
      if (value !== null && value !== undefined && !isValidNumber(value)) {
        flagged.push({
          type: 'batchRecord',
          record,
          anomalyType: 'type_mismatch',
          anomalyDescription: `${field} field contains non-numeric value`,
          identifier: getRecordIdentifier(record, 'batchRecord'),
          flaggedField: field
        });
      }
    });
  });

  // Check trace codes
  traceCodes.forEach(record => {
    if (record.quantityProduced !== null && !isValidNumber(record.quantityProduced)) {
      flagged.push({
        type: 'finishedProductTraceCode',
        record,
        anomalyType: 'type_mismatch',
        anomalyDescription: `Quantity produced field contains non-numeric value`,
        identifier: getRecordIdentifier(record, 'finishedProductTraceCode'),
        flaggedField: 'quantityProduced'
      });
    }
  });

  return flagged;
};

// Detect out-of-range temperatures
const detectOutOfRangeTemperatures = (
  ingredientReceiving: ShipmentEntry[],
  bakingProductionLogs: BakingProductionLog[],
  deliveryLogs: DeliveryLog[],
  batchRecords: BatchRecord[]
): FlaggedRecord[] => {
  const flagged: FlaggedRecord[] = [];

  // Check ingredient receiving temperatures
  ingredientReceiving.forEach(record => {
    const temp = record.receivingTemperature;
    if (temp !== null && temp !== undefined && typeof temp === 'number') {
      // Convert to Fahrenheit if needed
      const tempF = record.temperatureUnit === 'C' ? (temp * 9/5) + 32 : temp;
      let range: { min: number; max: number };
      let rangeLabel: string;
      let extraNote = '';

      switch (record.storageType) {
        case 'frozen':
          range = TEMPERATURE_RANGES.receivingFrozen;
          rangeLabel = '-20 to 0°F';
          // Special case: 0-10°F with frozen solid confirmed - still flag for review
          if (tempF > 0 && tempF <= 10 && record.frozenSolidConfirmed === true) {
            extraNote = ' (frozen solid confirmed by staff)';
          }
          break;
        case 'refrigerated':
          range = TEMPERATURE_RANGES.receivingRefrigerated;
          rangeLabel = '32-41°F';
          break;
        default:
          range = TEMPERATURE_RANGES.receivingNonRefrigerated;
          rangeLabel = '32-71°F';
          break;
      }

      if (tempF < range.min || tempF > range.max) {
        flagged.push({
          type: 'ingredientReceiving',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Receiving temperature ${temp}°${record.temperatureUnit} outside expected range (${rangeLabel})${extraNote}`,
          identifier: getRecordIdentifier(record, 'ingredientReceiving'),
          flaggedField: 'receivingTemperature'
        });
      }
    }
  });

  // Check baking production log oven temps
  bakingProductionLogs.forEach(record => {
    const temp = record.ovenTemp;
    if (temp !== null && temp !== undefined && typeof temp === 'number') {
      if (temp < TEMPERATURE_RANGES.oven.min || temp > TEMPERATURE_RANGES.oven.max) {
        flagged.push({
          type: 'bakingProductionLog',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Oven temperature ${temp}°F outside expected range (250-500°F)`,
          identifier: getRecordIdentifier(record, 'bakingProductionLog'),
          flaggedField: 'ovenTemp'
        });
      }
    }
  });

  // Check delivery log transportation temps
  deliveryLogs.forEach(record => {
    const temp = record.transportationTemp;
    if (temp !== null && temp !== undefined && typeof temp === 'number') {
      if (temp < TEMPERATURE_RANGES.transportFrozen.min || temp > TEMPERATURE_RANGES.transportFrozen.max) {
        flagged.push({
          type: 'deliveryLog',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Transportation temperature ${temp}°F outside expected range (-20 to 45°F)`,
          identifier: getRecordIdentifier(record, 'deliveryLog'),
          flaggedField: 'transportationTemp'
        });
      }
    }
  });

  // Check batch record temperatures
  batchRecords.forEach(record => {
    // Baking temp
    if (record.bakingActualTemp !== null && record.bakingActualTemp !== undefined) {
      const temp = record.bakingActualTemp;
      if (temp < TEMPERATURE_RANGES.baking.min || temp > TEMPERATURE_RANGES.baking.max) {
        flagged.push({
          type: 'batchRecord',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Baking temperature ${temp}°F outside expected range (210-240°F)`,
          identifier: getRecordIdentifier(record, 'batchRecord'),
          flaggedField: 'bakingActualTemp'
        });
      }
    }
    // Cooling 1 temp
    if (record.cooling1ActualTemp !== null && record.cooling1ActualTemp !== undefined) {
      const temp = record.cooling1ActualTemp;
      if (temp > TEMPERATURE_RANGES.cooling1.max) {
        flagged.push({
          type: 'batchRecord',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Cooling 1 temperature ${temp}°F exceeds limit (must be ≤70°F)`,
          identifier: getRecordIdentifier(record, 'batchRecord'),
          flaggedField: 'cooling1ActualTemp'
        });
      }
    }
    // Cooling 2 temp
    if (record.cooling2ActualTemp !== null && record.cooling2ActualTemp !== undefined) {
      const temp = record.cooling2ActualTemp;
      if (temp > TEMPERATURE_RANGES.cooling2.max) {
        flagged.push({
          type: 'batchRecord',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Cooling 2 temperature ${temp}°F exceeds limit (must be ≤39°F)`,
          identifier: getRecordIdentifier(record, 'batchRecord'),
          flaggedField: 'cooling2ActualTemp'
        });
      }
    }
    // Freezing temp
    if (record.freezingActualTemp !== null && record.freezingActualTemp !== undefined) {
      const temp = record.freezingActualTemp;
      if (temp < TEMPERATURE_RANGES.freezing.min || temp > TEMPERATURE_RANGES.freezing.max) {
        flagged.push({
          type: 'batchRecord',
          record,
          anomalyType: 'out_of_range_temperature',
          anomalyDescription: `Freezing temperature ${temp}°F outside expected range (-20 to 0°F)`,
          identifier: getRecordIdentifier(record, 'batchRecord'),
          flaggedField: 'freezingActualTemp'
        });
      }
    }
  });

  return flagged;
};

// Detect out-of-range quantities
const detectOutOfRangeQuantities = (
  ingredientReceiving: ShipmentEntry[],
  deliveryLogs: DeliveryLog[],
  traceCodes: TraceCode[]
): FlaggedRecord[] => {
  const flagged: FlaggedRecord[] = [];

  // Check ingredient receiving
  ingredientReceiving.forEach(record => {
    const qty = record.quantity;
    if (qty !== null && qty !== undefined && typeof qty === 'number') {
      if (qty < QUANTITY_RANGES.min || qty > QUANTITY_RANGES.max) {
        flagged.push({
          type: 'ingredientReceiving',
          record,
          anomalyType: 'out_of_range_quantity',
          anomalyDescription: `Quantity ${qty} outside expected range (0-10,000)`,
          identifier: getRecordIdentifier(record, 'ingredientReceiving'),
          flaggedField: 'quantity'
        });
      }
    }
  });

  // Check delivery logs
  deliveryLogs.forEach(record => {
    const qty = record.quantity;
    if (qty !== null && qty !== undefined && typeof qty === 'number') {
      if (qty < QUANTITY_RANGES.min || qty > QUANTITY_RANGES.max) {
        flagged.push({
          type: 'deliveryLog',
          record,
          anomalyType: 'out_of_range_quantity',
          anomalyDescription: `Quantity ${qty} outside expected range (0-10,000)`,
          identifier: getRecordIdentifier(record, 'deliveryLog'),
          flaggedField: 'quantity'
        });
      }
    }
  });

  // Check trace codes
  traceCodes.forEach(record => {
    const qty = record.quantityProduced;
    if (qty !== null && qty !== undefined && typeof qty === 'number') {
      if (qty < QUANTITY_RANGES.min || qty > QUANTITY_RANGES.max) {
        flagged.push({
          type: 'finishedProductTraceCode',
          record,
          anomalyType: 'out_of_range_quantity',
          anomalyDescription: `Quantity produced ${qty} outside expected range (0-10,000)`,
          identifier: getRecordIdentifier(record, 'finishedProductTraceCode'),
          flaggedField: 'quantityProduced'
        });
      }
    }
  });

  return flagged;
};

// Detect invalid time sequences (finish before start)
const detectInvalidTimeSequences = (
  bakingProductionLogs: BakingProductionLog[]
): FlaggedRecord[] => {
  const flagged: FlaggedRecord[] = [];

  bakingProductionLogs.forEach(record => {
    if (record.startTime && record.finishTime) {
      const start = new Date(record.startTime).getTime();
      const finish = new Date(record.finishTime).getTime();
      if (!isNaN(start) && !isNaN(finish) && finish < start) {
        flagged.push({
          type: 'bakingProductionLog',
          record,
          anomalyType: 'invalid_time_sequence',
          anomalyDescription: `Finish time is before start time`,
          identifier: getRecordIdentifier(record, 'bakingProductionLog'),
          flaggedField: 'finishTime'
        });
      }
    }
  });

  return flagged;
};

// Detect all anomalies across all record types
const detectAllAnomalies = (
  ingredientTraceCodes: IngredientTraceCode[],
  batchRecords: BatchRecord[],
  ingredientReceiving: ShipmentEntry[],
  bakingProductionLogs: BakingProductionLog[],
  deliveryLogs: DeliveryLog[],
  traceCodes: TraceCode[]
): { flaggedRecords: FlaggedRecord[]; flaggedIds: Set<string> } => {
  const allFlagged: FlaggedRecord[] = [];

  // Run all detection functions
  allFlagged.push(...findDuplicateLotNumbers(
    ingredientTraceCodes, batchRecords, ingredientReceiving,
    bakingProductionLogs, deliveryLogs, traceCodes
  ));
  allFlagged.push(...detectTypeMismatches(
    ingredientReceiving, bakingProductionLogs, deliveryLogs, batchRecords, traceCodes
  ));
  allFlagged.push(...detectOutOfRangeTemperatures(
    ingredientReceiving, bakingProductionLogs, deliveryLogs, batchRecords
  ));
  allFlagged.push(...detectOutOfRangeQuantities(
    ingredientReceiving, deliveryLogs, traceCodes
  ));
  allFlagged.push(...detectInvalidTimeSequences(bakingProductionLogs));

  // Deduplicate by record ID (a record might have multiple anomalies)
  const seenIds = new Set<string>();
  const uniqueFlagged: FlaggedRecord[] = [];
  allFlagged.forEach(item => {
    const key = `${item.type}-${item.record.id}`;
    if (!seenIds.has(key)) {
      seenIds.add(key);
      uniqueFlagged.push(item);
    }
  });

  // Collect all flagged IDs for exclusion from other sections
  const flaggedIds = new Set<string>();
  uniqueFlagged.forEach(item => flaggedIds.add(item.record.id));

  return { flaggedRecords: uniqueFlagged, flaggedIds };
};

interface CategorizedRecord {
  type: RecordType;
  record: IngredientTraceCode | BatchRecord;
  missingFields: string[];
}

// Required fields for ingredient trace codes (excluding verifiedBy and optional fields)
const INGREDIENT_TRACE_CODE_REQUIRED = [
  'supplierId',
  'ingredientName',
  'internalTraceCode',
  'dateReceived'
] as const;

// Required fields for batch records (excluding verifiedBy, supervisor fields, and optional fields)
const BATCH_RECORD_REQUIRED = [
  'productName',
  'productId',
  'batchLotNumber'
] as const;

// Field display names for better UX
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  supplierId: 'Supplier',
  ingredientName: 'Ingredient Name',
  internalTraceCode: 'Internal Trace Code',
  dateUsed: 'Date Used',
  finishedProduct: 'Finished Product',
  dateReceived: 'Date Received',
  verifiedBy: 'Verified By',
  productName: 'Product Name',
  productId: 'Product',
  batchLotNumber: 'Batch/Lot Number',
  productionStaffName: 'Production Staff',
  supervisorQaName: 'Supervisor Name',
  supervisorQaDate: 'Supervisor Date'
};

// Check if a field is missing (null, undefined, or empty string)
const isMissing = (value: unknown): boolean => {
  return value === null || value === undefined || value === '';
};

// Get missing required fields for ingredient trace code
const getIngredientTraceCodeMissingFields = (record: IngredientTraceCode): string[] => {
  const missing: string[] = [];
  for (const field of INGREDIENT_TRACE_CODE_REQUIRED) {
    if (isMissing(record[field])) {
      missing.push(field);
    }
  }
  // dateUsed and finishedProduct are only required if no batch usage exists
  const hasUsage = (record.usageCount !== undefined && record.usageCount > 0);
  if (!hasUsage && isMissing(record.dateUsed)) {
    missing.push('dateUsed');
  }
  if (!hasUsage && isMissing(record.finishedProduct)) {
    missing.push('finishedProduct');
  }
  return missing;
};

// Get missing required fields for batch record
const getBatchRecordMissingFields = (record: BatchRecord): string[] => {
  const missing: string[] = [];
  for (const field of BATCH_RECORD_REQUIRED) {
    if (isMissing(record[field])) {
      missing.push(field);
    }
  }
  // productionStaffName is required for production sign-off
  if (isMissing(record.productionStaffName)) {
    missing.push('productionStaffName');
  }
  return missing;
};

// Check if ingredient trace code is ready for verification
const isIngredientTraceCodeReadyForVerification = (record: IngredientTraceCode): boolean => {
  const missingRequired = getIngredientTraceCodeMissingFields(record);
  return missingRequired.length === 0 && isMissing(record.verifiedBy);
};

// Check if batch record is ready for verification
const isBatchRecordReadyForVerification = (record: BatchRecord): boolean => {
  const missingRequired = getBatchRecordMissingFields(record);
  if (missingRequired.length > 0) return false;

  // Ready if missing verifiedBy OR supervisor sign-off
  const missingVerifiedBy = isMissing(record.verifiedBy);
  const missingSupervisor = isMissing(record.supervisorQaName) || isMissing(record.supervisorQaDate);

  return missingVerifiedBy || missingSupervisor;
};

// Check if ingredient trace code is incomplete
const isIngredientTraceCodeIncomplete = (record: IngredientTraceCode): boolean => {
  return getIngredientTraceCodeMissingFields(record).length > 0;
};

// Check if batch record is incomplete
const isBatchRecordIncomplete = (record: BatchRecord): boolean => {
  return getBatchRecordMissingFields(record).length > 0;
};

// Get display name for record
const getRecordName = (item: CategorizedRecord): string => {
  if (item.type === 'ingredientTraceCode') {
    return (item.record as IngredientTraceCode).ingredientName || 'Unknown';
  }
  return (item.record as BatchRecord).productName || 'Unknown';
};

// Get lot/batch number for record
const getRecordLotNumber = (item: CategorizedRecord): string => {
  if (item.type === 'ingredientTraceCode') {
    return (item.record as IngredientTraceCode).internalTraceCode || '-';
  }
  return (item.record as BatchRecord).batchLotNumber || '-';
};

// Get date for record
const getRecordDate = (item: CategorizedRecord): string => {
  if (item.type === 'ingredientTraceCode') {
    return (item.record as IngredientTraceCode).dateUsed || '-';
  }
  return (item.record as BatchRecord).dateOfProduction || '-';
};

// Get type display name
const getTypeDisplayName = (type: RecordType): string => {
  return type === 'ingredientTraceCode' ? 'Ingredient Trace Code' : 'Batch Record';
};

// Format missing fields for display
const formatMissingFields = (fields: string[]): string => {
  return fields.map(f => FIELD_DISPLAY_NAMES[f] || f).join(', ');
};

export const AdminReview: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [verifyingIds, setVerifyingIds] = useState<Set<string>>(new Set());

  // Consolidated expanded state for all sections and sub-sections
  const [expandedSections, setExpandedSections] = useState({
    flagged: false,
    ready: false,
    incomplete: false,
    flaggedProductTracking: false,
    flaggedIngredientTracking: false,
    readyProductTracking: false,
    readyIngredientTracking: false,
    incompleteProductTracking: false,
    incompleteIngredientTracking: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // State for flagged records section
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<FlaggedRecord | null>(null);

  // Get data from Redux store - original 2 types
  const ingredientTraceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);
  const batchRecords = useAppSelector(state => state.batchRecords.records);
  const currentUser = useAppSelector(state => state.auth.user);

  // Get data from Redux store - additional 4 HACCP log types
  const ingredientReceiving = useAppSelector(state => state.ingredientReceiving.entries);
  const bakingProductionLogs = useAppSelector(state => state.bakingProductionLog.logs);
  const deliveryLogs = useAppSelector(state => state.deliveryLogs.logs);
  const traceCodes = useAppSelector(state => state.traceCodes.traceCodes);

  // Compute flagged records from all 6 HACCP log types
  const { flaggedRecords, flaggedIds } = useMemo(() => {
    return detectAllAnomalies(
      ingredientTraceCodes,
      batchRecords,
      ingredientReceiving,
      bakingProductionLogs,
      deliveryLogs,
      traceCodes
    );
  }, [ingredientTraceCodes, batchRecords, ingredientReceiving, bakingProductionLogs, deliveryLogs, traceCodes]);

  // Filter flagged records by category
  const flaggedProductRecords = useMemo(() =>
    flaggedRecords.filter(item => isProductTrackingType(item.type)), [flaggedRecords]);
  const flaggedIngredientRecords = useMemo(() =>
    flaggedRecords.filter(item => isIngredientTrackingType(item.type)), [flaggedRecords]);

  // Get extended type display name for all 6 types
  const getExtendedTypeDisplayName = (type: ExtendedRecordType): string => {
    const names: Record<ExtendedRecordType, string> = {
      ingredientTraceCode: 'Ingredient Trace Code',
      batchRecord: 'Batch Record',
      ingredientReceiving: 'Ingredient Receiving',
      bakingProductionLog: 'Baking Production Log',
      deliveryLog: 'Delivery Log',
      finishedProductTraceCode: 'Finished Product Trace Code'
    };
    return names[type];
  };

  // Handle edit action for flagged records - navigate to appropriate page
  const handleEditFlagged = (item: FlaggedRecord) => {
    const fieldParam = item.flaggedField ? `&field=${item.flaggedField}&flagged=true` : '';
    const routes: Record<ExtendedRecordType, string> = {
      ingredientTraceCode: `/haccp/ingredient-tracking?open=${item.record.id}${fieldParam}`,
      batchRecord: `/haccp/product-tracking?open=${item.record.id}${fieldParam}`,
      ingredientReceiving: `/haccp/ingredient-tracking?openReceiving=${item.record.id}${fieldParam}`,
      bakingProductionLog: `/haccp/product-tracking?openBaking=${item.record.id}${fieldParam}`,
      finishedProductTraceCode: `/haccp/product-tracking?openTraceCode=${item.record.id}${fieldParam}`,
      deliveryLog: `/haccp/product-tracking?openDelivery=${item.record.id}${fieldParam}`
    };
    navigate(routes[item.type]);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (item: FlaggedRecord) => {
    setRecordToDelete(item);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteFlagged = async () => {
    if (!recordToDelete) return;

    const recordId = recordToDelete.record.id;
    setDeletingIds(prev => new Set(prev).add(recordId));

    try {
      switch (recordToDelete.type) {
        case 'ingredientTraceCode':
          await dispatch(deleteIngredientTraceCode(recordId)).unwrap();
          break;
        case 'batchRecord':
          await dispatch(deleteBatchRecordAsync(recordId)).unwrap();
          break;
        case 'ingredientReceiving':
          await dispatch(deleteIngredientReceivingEntry(recordId)).unwrap();
          break;
        case 'bakingProductionLog':
          await dispatch(deleteBakingProductionLogAsync(recordId)).unwrap();
          break;
        case 'deliveryLog':
          await dispatch(deleteDeliveryLogAsync(recordId)).unwrap();
          break;
        case 'finishedProductTraceCode':
          await dispatch(deleteTraceCodeAsync(recordId)).unwrap();
          break;
      }
      dispatch(addToast(`Deleted flagged ${getExtendedTypeDisplayName(recordToDelete.type)}`, 'success'));
    } catch (error) {
      console.error('Failed to delete record:', error);
      dispatch(addToast('Failed to delete record', 'error'));
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      setDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  // Handle edit action - navigate to the appropriate page with field-level deep-linking
  const handleEdit = (item: CategorizedRecord) => {
    const fieldParam = item.missingFields.length > 0 ? `&field=${item.missingFields[0]}` : '';
    if (item.type === 'ingredientTraceCode') {
      navigate(`/haccp/ingredient-tracking?open=${item.record.id}${fieldParam}`);
    } else {
      navigate(`/haccp/product-tracking?open=${item.record.id}${fieldParam}`);
    }
  };

  // Handle verify action
  const handleVerify = async (item: CategorizedRecord) => {
    const recordId = item.record.id;
    const verifierName = currentUser?.name || 'Admin';
    const today = new Date().toISOString().split('T')[0];

    // Add to verifying set
    setVerifyingIds(prev => new Set(prev).add(recordId));

    try {
      if (item.type === 'ingredientTraceCode') {
        const record = item.record as IngredientTraceCode;
        const updatedRecord: IngredientTraceCode = {
          ...record,
          verifiedBy: verifierName
        };
        await dispatch(updateIngredientTraceCode(updatedRecord)).unwrap();
        dispatch(addToast(`Verified: ${record.ingredientName}`, 'success'));
      } else {
        const record = item.record as BatchRecord;
        const updatedRecord: BatchRecord = {
          ...record,
          verifiedBy: isMissing(record.verifiedBy) ? verifierName : record.verifiedBy,
          supervisorQaName: isMissing(record.supervisorQaName) ? verifierName : record.supervisorQaName,
          supervisorQaDate: isMissing(record.supervisorQaDate) ? today : record.supervisorQaDate
        };
        await dispatch(updateBatchRecordAsync(updatedRecord)).unwrap();
        dispatch(addToast(`Verified: ${record.productName}`, 'success'));
      }
    } catch (error) {
      console.error('Failed to verify record:', error);
      dispatch(addToast('Failed to verify record', 'error'));
    } finally {
      // Remove from verifying set
      setVerifyingIds(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
    }
  };

  // Categorize records (excluding flagged records)
  const { readyRecords, incompleteRecords } = useMemo(() => {
    const ready: CategorizedRecord[] = [];
    const incomplete: CategorizedRecord[] = [];

    // Process ingredient trace codes - skip flagged records
    for (const record of ingredientTraceCodes) {
      if (flaggedIds.has(record.id)) continue; // Skip flagged records

      if (isIngredientTraceCodeIncomplete(record)) {
        incomplete.push({
          type: 'ingredientTraceCode',
          record,
          missingFields: getIngredientTraceCodeMissingFields(record)
        });
      } else if (isIngredientTraceCodeReadyForVerification(record)) {
        ready.push({
          type: 'ingredientTraceCode',
          record,
          missingFields: ['verifiedBy']
        });
      }
    }

    // Process batch records - skip flagged records
    for (const record of batchRecords) {
      if (flaggedIds.has(record.id)) continue; // Skip flagged records

      if (isBatchRecordIncomplete(record)) {
        incomplete.push({
          type: 'batchRecord',
          record,
          missingFields: getBatchRecordMissingFields(record)
        });
      } else if (isBatchRecordReadyForVerification(record)) {
        const missing: string[] = [];
        if (isMissing(record.verifiedBy)) missing.push('verifiedBy');
        if (isMissing(record.supervisorQaName)) missing.push('supervisorQaName');
        if (isMissing(record.supervisorQaDate)) missing.push('supervisorQaDate');
        ready.push({
          type: 'batchRecord',
          record,
          missingFields: missing
        });
      }
    }

    return { readyRecords: ready, incompleteRecords: incomplete };
  }, [ingredientTraceCodes, batchRecords, flaggedIds]);

  // Filter ready/incomplete by category (these only have 2 types: ingredientTraceCode, batchRecord)
  const readyProductRecords = useMemo(() =>
    readyRecords.filter(item => item.type === 'batchRecord'), [readyRecords]);
  const readyIngredientRecords = useMemo(() =>
    readyRecords.filter(item => item.type === 'ingredientTraceCode'), [readyRecords]);
  const incompleteProductRecords = useMemo(() =>
    incompleteRecords.filter(item => item.type === 'batchRecord'), [incompleteRecords]);
  const incompleteIngredientRecords = useMemo(() =>
    incompleteRecords.filter(item => item.type === 'ingredientTraceCode'), [incompleteRecords]);

  // Styles
  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #e0e0e0',
    cursor: 'pointer',
    userSelect: 'none'
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#333'
  };

  const chevronStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#666'
  };

  const sectionContentStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'white'
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '24px',
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic'
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  };

  const thStyle: React.CSSProperties = {
    padding: '12px 8px',
    backgroundColor: '#f8f9fa',
    fontWeight: '600',
    textAlign: 'left',
    borderBottom: '2px solid #e0e0e0',
    textTransform: 'uppercase',
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: '#555'
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px 8px',
    borderBottom: '1px solid #eee'
  };

  const typeBadgeStyle = (type: RecordType): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    backgroundColor: type === 'ingredientTraceCode' ? '#e3f2fd' : '#fff3e0',
    color: type === 'ingredientTraceCode' ? '#1565c0' : '#e65100'
  });

  // Extended type badge style for all 6 HACCP log types
  const extendedTypeBadgeStyle = (type: ExtendedRecordType): React.CSSProperties => {
    const colors: Record<ExtendedRecordType, { bg: string; text: string }> = {
      ingredientTraceCode: { bg: '#e3f2fd', text: '#1565c0' },
      batchRecord: { bg: '#fff3e0', text: '#e65100' },
      ingredientReceiving: { bg: '#e8f5e9', text: '#2e7d32' },
      bakingProductionLog: { bg: '#fce4ec', text: '#c2185b' },
      deliveryLog: { bg: '#f3e5f5', text: '#7b1fa2' },
      finishedProductTraceCode: { bg: '#e0f7fa', text: '#00838f' }
    };
    const color = colors[type];
    return {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '500',
      backgroundColor: color.bg,
      color: color.text
    };
  };

  // Flagged section header style (amber/warning)
  const flaggedSectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#fff8e1',
    borderBottom: '2px solid #ffc107',
    cursor: 'pointer',
    userSelect: 'none'
  };

  // Delete button style
  const deleteButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#d32f2f',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    marginLeft: '8px'
  };

  // Fix button style (same as edit but for flagged)
  const fixButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  };

  const verifyButtonStyle = (isVerifying: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    backgroundColor: isVerifying ? '#a5d6a7' : '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: isVerifying ? 'not-allowed' : 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    opacity: isVerifying ? 0.7 : 1
  });

  const editButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  };

  const missingFieldsStyle: React.CSSProperties = {
    color: '#d32f2f',
    fontSize: '13px'
  };

  // Nested sub-section styles
  const nestedSectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    userSelect: 'none'
  };

  const nestedSectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '500',
    fontSize: '13px',
    color: '#555'
  };

  const nestedChevronStyle = (isExpanded: boolean): React.CSSProperties => ({
    fontSize: '10px',
    color: '#666',
    transition: 'transform 0.2s',
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
    display: 'inline-block'
  });

  const nestedEmptyStateStyle: React.CSSProperties = {
    padding: '16px',
    textAlign: 'center',
    color: '#888',
    fontStyle: 'italic',
    fontSize: '13px'
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
        HACCP Admin Review Dashboard
      </h1>

      {/* Flagged for Review Section */}
      <div style={{ marginBottom: '16px', border: '1px solid #ffc107', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          style={flaggedSectionHeaderStyle}
          onClick={() => toggleSection('flagged')}
        >
          <span style={{ ...sectionTitleStyle, color: '#f57c00' }}>
            <span style={chevronStyle}>{expandedSections.flagged ? '▼' : '▶'}</span>
            Flagged for Review ({flaggedRecords.length} records)
          </span>
        </div>
        {expandedSections.flagged && (
          <div style={sectionContentStyle}>
            {flaggedRecords.length === 0 ? (
              <div style={emptyStateStyle}>
                No records flagged for review
              </div>
            ) : (
              <>
                {/* Product Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('flaggedProductTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.flaggedProductTracking)}>▶</span>
                      Product Tracking ({flaggedProductRecords.length})
                    </span>
                  </div>
                  {expandedSections.flaggedProductTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {flaggedProductRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No flagged product tracking records</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Identifier</th>
                              <th style={thStyle}>Anomaly Description</th>
                              <th style={{ ...thStyle, width: '150px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flaggedProductRecords.map((item) => {
                              const isDeleting = deletingIds.has(item.record.id);
                              return (
                                <tr key={`${item.type}-${item.record.id}`}>
                                  <td style={tdStyle}>
                                    <span style={extendedTypeBadgeStyle(item.type)}>
                                      {getExtendedTypeDisplayName(item.type)}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>{item.identifier}</td>
                                  <td style={{ ...tdStyle, color: '#d32f2f' }}>
                                    {item.anomalyDescription}
                                  </td>
                                  <td style={tdStyle}>
                                    <button
                                      style={fixButtonStyle}
                                      onClick={() => handleEditFlagged(item)}
                                    >
                                      View
                                    </button>
                                    <button
                                      style={{
                                        ...deleteButtonStyle,
                                        opacity: isDeleting ? 0.7 : 1,
                                        cursor: isDeleting ? 'not-allowed' : 'pointer'
                                      }}
                                      onClick={() => handleDeleteClick(item)}
                                      disabled={isDeleting}
                                    >
                                      {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Ingredient Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('flaggedIngredientTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.flaggedIngredientTracking)}>▶</span>
                      Ingredient Tracking ({flaggedIngredientRecords.length})
                    </span>
                  </div>
                  {expandedSections.flaggedIngredientTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {flaggedIngredientRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No flagged ingredient tracking records</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Identifier</th>
                              <th style={thStyle}>Anomaly Description</th>
                              <th style={{ ...thStyle, width: '150px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flaggedIngredientRecords.map((item) => {
                              const isDeleting = deletingIds.has(item.record.id);
                              return (
                                <tr key={`${item.type}-${item.record.id}`}>
                                  <td style={tdStyle}>
                                    <span style={extendedTypeBadgeStyle(item.type)}>
                                      {getExtendedTypeDisplayName(item.type)}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>{item.identifier}</td>
                                  <td style={{ ...tdStyle, color: '#d32f2f' }}>
                                    {item.anomalyDescription}
                                  </td>
                                  <td style={tdStyle}>
                                    <button
                                      style={fixButtonStyle}
                                      onClick={() => handleEditFlagged(item)}
                                    >
                                      View
                                    </button>
                                    <button
                                      style={{
                                        ...deleteButtonStyle,
                                        opacity: isDeleting ? 0.7 : 1,
                                        cursor: isDeleting ? 'not-allowed' : 'pointer'
                                      }}
                                      onClick={() => handleDeleteClick(item)}
                                      disabled={isDeleting}
                                    >
                                      {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Ready for Verification Section */}
      <div style={{ marginBottom: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          style={sectionHeaderStyle}
          onClick={() => toggleSection('ready')}
        >
          <span style={sectionTitleStyle}>
            <span style={chevronStyle}>{expandedSections.ready ? '▼' : '▶'}</span>
            Ready for Verification ({readyRecords.length} records)
          </span>
        </div>
        {expandedSections.ready && (
          <div style={sectionContentStyle}>
            {readyRecords.length === 0 ? (
              <div style={emptyStateStyle}>
                No records need verification
              </div>
            ) : (
              <>
                {/* Product Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('readyProductTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.readyProductTracking)}>▶</span>
                      Product Tracking ({readyProductRecords.length})
                    </span>
                  </div>
                  {expandedSections.readyProductTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {readyProductRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No product tracking records ready for verification</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Name/Product</th>
                              <th style={thStyle}>Lot/Batch #</th>
                              <th style={thStyle}>Date</th>
                              <th style={thStyle}>Missing</th>
                              <th style={{ ...thStyle, width: '100px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {readyProductRecords.map((item) => {
                              const isVerifying = verifyingIds.has(item.record.id);
                              return (
                                <tr key={`${item.type}-${item.record.id}`}>
                                  <td style={tdStyle}>
                                    <span style={typeBadgeStyle(item.type)}>
                                      {getTypeDisplayName(item.type)}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>{getRecordName(item)}</td>
                                  <td style={tdStyle}>{getRecordLotNumber(item)}</td>
                                  <td style={tdStyle}>{getRecordDate(item)}</td>
                                  <td style={{ ...tdStyle, ...missingFieldsStyle }}>
                                    {formatMissingFields(item.missingFields)}
                                  </td>
                                  <td style={tdStyle}>
                                    <button
                                      style={verifyButtonStyle(isVerifying)}
                                      onClick={() => handleVerify(item)}
                                      disabled={isVerifying}
                                    >
                                      {isVerifying ? 'Verifying...' : 'Verify'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Ingredient Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('readyIngredientTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.readyIngredientTracking)}>▶</span>
                      Ingredient Tracking ({readyIngredientRecords.length})
                    </span>
                  </div>
                  {expandedSections.readyIngredientTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {readyIngredientRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No ingredient tracking records ready for verification</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Name/Product</th>
                              <th style={thStyle}>Lot/Batch #</th>
                              <th style={thStyle}>Date</th>
                              <th style={thStyle}>Missing</th>
                              <th style={{ ...thStyle, width: '100px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {readyIngredientRecords.map((item) => {
                              const isVerifying = verifyingIds.has(item.record.id);
                              return (
                                <tr key={`${item.type}-${item.record.id}`}>
                                  <td style={tdStyle}>
                                    <span style={typeBadgeStyle(item.type)}>
                                      {getTypeDisplayName(item.type)}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>{getRecordName(item)}</td>
                                  <td style={tdStyle}>{getRecordLotNumber(item)}</td>
                                  <td style={tdStyle}>{getRecordDate(item)}</td>
                                  <td style={{ ...tdStyle, ...missingFieldsStyle }}>
                                    {formatMissingFields(item.missingFields)}
                                  </td>
                                  <td style={tdStyle}>
                                    <button
                                      style={verifyButtonStyle(isVerifying)}
                                      onClick={() => handleVerify(item)}
                                      disabled={isVerifying}
                                    >
                                      {isVerifying ? 'Verifying...' : 'Verify'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Incomplete Records Section */}
      <div style={{ marginBottom: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          style={sectionHeaderStyle}
          onClick={() => toggleSection('incomplete')}
        >
          <span style={sectionTitleStyle}>
            <span style={chevronStyle}>{expandedSections.incomplete ? '▼' : '▶'}</span>
            Incomplete Records ({incompleteRecords.length} records)
          </span>
        </div>
        {expandedSections.incomplete && (
          <div style={sectionContentStyle}>
            {incompleteRecords.length === 0 ? (
              <div style={emptyStateStyle}>
                No incomplete records
              </div>
            ) : (
              <>
                {/* Product Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('incompleteProductTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.incompleteProductTracking)}>▶</span>
                      Product Tracking ({incompleteProductRecords.length})
                    </span>
                  </div>
                  {expandedSections.incompleteProductTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {incompleteProductRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No incomplete product tracking records</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Name/Product</th>
                              <th style={thStyle}>Lot/Batch #</th>
                              <th style={thStyle}>Missing Fields</th>
                              <th style={{ ...thStyle, width: '100px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {incompleteProductRecords.map((item) => (
                              <tr key={`${item.type}-${item.record.id}`}>
                                <td style={tdStyle}>
                                  <span style={typeBadgeStyle(item.type)}>
                                    {getTypeDisplayName(item.type)}
                                  </span>
                                </td>
                                <td style={tdStyle}>{getRecordName(item)}</td>
                                <td style={tdStyle}>{getRecordLotNumber(item)}</td>
                                <td style={{ ...tdStyle, ...missingFieldsStyle }}>
                                  {formatMissingFields(item.missingFields)}
                                </td>
                                <td style={tdStyle}>
                                  <button
                                    style={editButtonStyle}
                                    onClick={() => handleEdit(item)}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Ingredient Tracking Sub-section */}
                <div style={{ marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={nestedSectionHeaderStyle} onClick={() => toggleSection('incompleteIngredientTracking')}>
                    <span style={nestedSectionTitleStyle}>
                      <span style={nestedChevronStyle(expandedSections.incompleteIngredientTracking)}>▶</span>
                      Ingredient Tracking ({incompleteIngredientRecords.length})
                    </span>
                  </div>
                  {expandedSections.incompleteIngredientTracking && (
                    <div style={{ padding: '12px', overflowX: 'auto' }}>
                      {incompleteIngredientRecords.length === 0 ? (
                        <div style={nestedEmptyStateStyle}>No incomplete ingredient tracking records</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Type</th>
                              <th style={thStyle}>Name/Product</th>
                              <th style={thStyle}>Lot/Batch #</th>
                              <th style={thStyle}>Missing Fields</th>
                              <th style={{ ...thStyle, width: '100px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {incompleteIngredientRecords.map((item) => (
                              <tr key={`${item.type}-${item.record.id}`}>
                                <td style={tdStyle}>
                                  <span style={typeBadgeStyle(item.type)}>
                                    {getTypeDisplayName(item.type)}
                                  </span>
                                </td>
                                <td style={tdStyle}>{getRecordName(item)}</td>
                                <td style={tdStyle}>{getRecordLotNumber(item)}</td>
                                <td style={{ ...tdStyle, ...missingFieldsStyle }}>
                                  {formatMissingFields(item.missingFields)}
                                </td>
                                <td style={tdStyle}>
                                  <button
                                    style={editButtonStyle}
                                    onClick={() => handleEdit(item)}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteRecordConfirmationModal
        isOpen={deleteModalOpen}
        recordType={recordToDelete ? getExtendedTypeDisplayName(recordToDelete.type) : ''}
        recordIdentifier={recordToDelete?.identifier || ''}
        anomalyDescription={recordToDelete?.anomalyDescription || ''}
        onConfirm={handleDeleteFlagged}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};
